import { AccessibilitySelector } from "./FilterPanels/AccessibilitySelector";
import { DateRangeSelector } from "./FilterPanels/DateRangeSelector/DateRangeSelector";
import { OpponentSelector } from "./FilterPanels/OpponentSelector";
import { PriceRangeSelector } from "./FilterPanels/PriceRangeSelector";
import { SeatCounter } from "./FilterPanels/SeatCounter";
import { DynamicSeatCount } from "./FilterPanels/DynamicSeatCount";
import { SectionSelector } from "./FilterPanels/SectionSelector";
import { FilterSelectorFooter } from "./game_filters_helpers";
import React from "react";
import { useRouter } from "next/router";
import {
  filterPanelNames,
  inventoryTypeUIDs,
  purchaseSeatsTabUIDs as tabUIDs,
} from "@/global/config/constants";
import { useFilterApis } from "./useFilterApis";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useDispatch } from "react-redux";
import {
  gameFiltersState,
  selectCategories,
} from "@/global/store/data/gameFilters";
import { useSelectActiveEvent } from "../PurchaseSeats/ChooseSeats/useSelectActiveEvent";
import { createState } from "@/global/store/zustand_store";
import { selectCartManager } from "@/global/store/data/purchaseSeats/cartManager";
import { useAppStore } from "@/global/store";
import { calcDefaultPurchaseSeatTab } from "../PurchaseSeats/utils.purchaseSeats";
import { CategorySelector } from "./FilterPanels/CategorySelector";

export const useFilterPanelsState = createState({
  isDisabledSeatCountChange: false,
  lastChangedFilter: "",
});

let isFirstTime = true;

export const filterSelectorTitles = {
  opponent_selector: "Opponents",
  date_range_selector: "Days",
  seat_counter: "Number of Seats",
  dynamic_seat_count: "Number of Seats",
  price_selector: "Price Range",
  accessibility_selector: "Accessibility",
  section_selector: "Section",
  category_selector: "Category",
};

export function useFilterPanels() {
  const { md: mdDown } = useCssMediaQuery("down");
  const dispatch = useDispatch();
  const router = useRouter();
  const isPreviewCheckout = Boolean(router?.query?.isPreviewCheckout);
  const teamConfig = useTeamConfig();
  const {
    purchaseSeatConfigs: { minSeatLimit, defaultSeatCount },
    uiConfigs,
    inventoryType,
    languages,
  } = teamConfig;

  if (languages.opponentSelectorTitle) {
    filterSelectorTitles.opponent_selector = languages.opponentSelectorTitle;
  }
  if (languages?.gameFilters?.seatCounter?.title) {
    filterSelectorTitles.seat_counter = languages.gameFilters.seatCounter.title;
  }
  if (languages?.gameFilters?.priceRange?.title) {
    filterSelectorTitles.price_selector =
      languages.gameFilters.priceRange.title;
  }
  if (languages?.gameFilters?.accessibility?.title) {
    filterSelectorTitles.accessibility_selector =
      languages.gameFilters.accessibility.title;
  }
  if (languages?.gameFilters?.category?.title) {
    filterSelectorTitles.category_selector =
      languages.gameFilters.category.title;
  }

  const { handleGetEventFilters } = useFilterApis();
  const purchaseSeatTab = router.query.purchaseSeatTab as any;
  const { activeEvent } = useSelectActiveEvent();
  const [selectedFilter, setSelectedFilter] = React.useState("");
  const [isFooterEnabled, setIsFooterEnabled] = React.useState(true);
  const [isDisabledInteractiveToggle, setIsDisabledInteractiveToggle] =
    React.useState(true);
  const filterPanelsState = useFilterPanelsState();
  const cartState = useAppStore(selectCartManager);
  const categories = useAppStore(selectCategories);

  const handleSelectFilter = (filterName: string) => {
    setSelectedFilter(filterName);
  };

  const handleClearFilters = () => {
    dispatch(gameFiltersState.actions.clearEventFilters());
  };

  React.useEffect(() => {
    handleGetEventFilters();

    if (uiConfigs?.gameFiltersSettings?.dynamicSeatCount?.length) {
      dispatch(
        gameFiltersState.actions.setDynamicSeatCount(
          uiConfigs?.gameFiltersSettings?.dynamicSeatCount?.map(
            ({ id, seatCount, orderIndex }) => ({ id, seatCount, orderIndex })
          ) || []
        )
      );
    } else {
      // Use regular seat counter
      dispatch(
        gameFiltersState.actions.setSeatCount(defaultSeatCount || minSeatLimit)
      );
    }

    dispatch(gameFiltersState.actions.setInitialSeatCountHydrated(true));
  }, []);

  React.useEffect(() => {
    if (isFirstTime && purchaseSeatTab === tabUIDs.chooseSeats && activeEvent) {
      if (
        !isPreviewCheckout &&
        inventoryType !== inventoryTypeUIDs.suitLevel &&
        inventoryType !== inventoryTypeUIDs?.suiteLevelV2
      ) {
        // Choose the appropriate seat counter based on team config
        const seatCounterFilter = uiConfigs?.gameFiltersSettings
          ?.dynamicSeatCount?.length
          ? "dynamic_seat_count"
          : "seat_counter";
        setSelectedFilter(seatCounterFilter);
      }
      isFirstTime = false;
    }
    if (
      purchaseSeatTab !== tabUIDs.chooseSeats &&
      (selectedFilter == "seat_counter" ||
        selectedFilter == "dynamic_seat_count" ||
        selectedFilter == "price_selector" ||
        selectedFilter == "accessibility_selector" ||
        selectedFilter == "section_selector")
    ) {
      setSelectedFilter("");
    }
  }, [purchaseSeatTab, activeEvent]);

  React.useEffect(() => {
    const isConfigSeatCounterDisabled = teamConfig?.uiConfigs?.gameFiltersSettings?.disableSeatCounterAfterFirstEventHeld; // prettier-ignore
    if (isConfigSeatCounterDisabled) {
      const isHeldEvent = cartState?.cart?.some((evt) => evt?.isHeld);
      const isChooseSeatsTab = purchaseSeatTab === tabUIDs.chooseSeats;
      const defaultTab = calcDefaultPurchaseSeatTab(teamConfig);
      // prettier-ignore
      if (isHeldEvent && isChooseSeatsTab && !filterPanelsState.isDisabledSeatCountChange) {
        filterPanelsState.set({ isDisabledSeatCountChange: true });
      }

      if (purchaseSeatTab === defaultTab || !isHeldEvent) {
        filterPanelsState.set({ isDisabledSeatCountChange: false });
      }
    }
  }, [purchaseSeatTab, cartState]);

  const filterPanels = [
    {
      name: filterPanelNames.category_selector,
      component: (
        <CategorySelector
          title={!mdDown ? filterSelectorTitles.category_selector : ""}
        />
      ),
      isHidden:
        purchaseSeatTab !== tabUIDs.findGames || categories?.length <= 1,
      isDisabled: false,
    },
    {
      name: filterPanelNames.opponent_selector,
      component: (
        <OpponentSelector
          title={!mdDown ? filterSelectorTitles.opponent_selector : ""}
        />
      ),
      isHidden:
        purchaseSeatTab !== tabUIDs.findGames ||
        Boolean(uiConfigs?.gameFiltersSettings?.hideOpponentFilter),
      isDisabled: false,
    },
    {
      name: filterPanelNames.date_range_selector,
      title: "Days",
      component: (
        <DateRangeSelector
          title={!mdDown ? filterSelectorTitles.date_range_selector : ""}
        />
      ),
      isHidden: purchaseSeatTab !== tabUIDs.findGames,
      isDisabled: false,
    },
    {
      name: filterPanelNames.seat_counter,
      component: (
        <SeatCounter
          title={!mdDown ? filterSelectorTitles.seat_counter : ""}
          description={languages?.gameFilters?.seatCounter?.description || ""}
        />
      ),

      footerComponent: isFooterEnabled ? (
        <FilterSelectorFooter
          onClickClose={() => {
            setIsDisabledInteractiveToggle(false);
            setIsFooterEnabled(false);
            handleSelectFilter("");
          }}
          onClickNext={() => handleSelectFilter("price_selector")}
        />
      ) : null,
      isHidden:
        purchaseSeatTab !== tabUIDs.chooseSeats ||
        inventoryType === inventoryTypeUIDs.suitLevel ||
        inventoryType === inventoryTypeUIDs.suiteLevelV2 ||
        Boolean(uiConfigs?.gameFiltersSettings?.dynamicSeatCount?.length), // Hide when categorized is enabled
      isDisabled: filterPanelsState.isDisabledSeatCountChange,
    },
    {
      name: filterPanelNames.dynamic_seat_count,
      component: <DynamicSeatCount />,
      footerComponent: isFooterEnabled ? (
        <FilterSelectorFooter
          onClickClose={() => {
            setIsDisabledInteractiveToggle(false);
            setIsFooterEnabled(false);
            handleSelectFilter("");
          }}
          onClickNext={() => handleSelectFilter("price_selector")}
        />
      ) : null,
      isHidden:
        purchaseSeatTab !== tabUIDs.chooseSeats ||
        inventoryType === inventoryTypeUIDs.suitLevel ||
        inventoryType === inventoryTypeUIDs.suiteLevelV2 ||
        !Boolean(uiConfigs?.gameFiltersSettings?.dynamicSeatCount?.length), // Show only when categorized is enabled
      isDisabled: filterPanelsState.isDisabledSeatCountChange,
    },
    {
      name: filterPanelNames.price_selector,
      component: (
        <PriceRangeSelector
          title={!mdDown ? filterSelectorTitles.price_selector : ""}
        />
      ),
      footerComponent: isFooterEnabled ? (
        <FilterSelectorFooter
          onClickClose={() => {
            setIsDisabledInteractiveToggle(false);
            setIsFooterEnabled(false);
            handleSelectFilter("");
          }}
        />
      ) : null,
      isHidden:
        purchaseSeatTab !== tabUIDs.chooseSeats ||
        inventoryType === inventoryTypeUIDs.suitLevel ||
        inventoryType === inventoryTypeUIDs.suiteLevelV2,
      isDisabled: false,
    },
    {
      name: filterPanelNames.accessibility_selector,
      component: (
        <AccessibilitySelector
          title={!mdDown ? filterSelectorTitles.accessibility_selector : ""}
          description={languages?.gameFilters?.accessibility?.description || ""}
          customLabel={uiConfigs.customAdaLabel ?? undefined}
        />
      ),
      isHidden:
        purchaseSeatTab !== tabUIDs.chooseSeats ||
        inventoryType === inventoryTypeUIDs.suitLevel ||
        inventoryType === inventoryTypeUIDs.suiteLevelV2 ||
        Boolean(uiConfigs?.gameFiltersSettings?.hideAccessibilityFilter),
      isDisabled: false,
    },
    {
      name: filterPanelNames.section_selector,
      title: "Section",
      component: (
        <SectionSelector
          title={!mdDown ? filterSelectorTitles.section_selector : ""}
        />
      ),
      isHidden:
        purchaseSeatTab !== tabUIDs.chooseSeats ||
        Boolean(uiConfigs?.gameFiltersSettings?.hideSectionFilter),
      isDisabled: false,
    },
  ];

  return {
    filterPanels,
    handleSelectFilter,
    handleClearFilters,
    selectedFilter,
    isDisabledInteractiveToggle,
  };
}
